import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';

// ----------------------------------------------------------------------

/**
 * Per-route error boundary.
 *
 * The app has had exactly one boundary — `Sentry.ErrorBoundary` around the
 * whole tree in `app.jsx` — so a render-time throw anywhere replaced the
 * ENTIRE application with the generic error screen, navigation included. On
 * 2026-09-28 one unguarded property read on the integrations page
 * (JAVASCRIPT-REACT-195) did exactly that, and because the throw recurred on
 * every render the page could not be escaped by reloading.
 *
 * Scoping a boundary to the routed page keeps the failure where it happened:
 * the sidebar, the practice switcher and every other page keep working, and
 * the error still reaches Sentry through the same integration. The app-level
 * boundary stays as the backstop for anything outside the routed page.
 *
 * Keyed on pathname so navigating away from a broken page resets it —
 * without the key React keeps a boundary latched in its error state and the
 * next page renders the fallback too.
 */
export default function RouteErrorBoundary({ children }) {
    const { pathname } = useLocation();

    return (
        <Sentry.ErrorBoundary key={pathname} fallback={<RouteErrorFallback />}>
            {children}
        </Sentry.ErrorBoundary>
    );
}

RouteErrorBoundary.propTypes = {
    children: PropTypes.node,
};

function RouteErrorFallback() {
    return (
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
            <Paper variant="outlined" sx={{ p: 4, maxWidth: 480, width: '100%' }}>
                <Stack spacing={2}>
                    <Typography variant="h6">This page could not be displayed</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Something on this page failed to load. The rest of the app is
                        unaffected — you can carry on elsewhere, or try this page again.
                        The problem has been reported automatically.
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        <Button variant="contained" onClick={() => window.location.reload()}>
                            Try again
                        </Button>
                    </Stack>
                </Stack>
            </Paper>
        </Box>
    );
}
